// @ts-strict-ignore
import { lastValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { CustomArticleInfoVO, CustomArticleLibraryVO, CustomImageDataVO, SaveCustomArticleRequestVO } from '@project/components/article/model/vo/custom-article.vo';
import { ImageProcessingData } from '@3d-scene/processor/image-processing-data';
import { Log } from '@shared/debug/log';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CustomArticleService {
  private _customArticlesChanged$: Subject<void> = new Subject<void>();

  private saveCustomImageSubject: Subject<string> = new Subject<string>();

  constructor(
    private modelConversionService: ModelConversionService,
    private wagoApiClient: WagoApiClient
  ) {}

  public get customArticlesChanged$(): Observable<void> {
    return this._customArticlesChanged$.asObservable();
  }

  public get saveCustomImage$(): Observable<string> {
    return this.saveCustomImageSubject.asObservable();
  }

  public getCustomArticleLibrariesByUserId(userId: number): Observable<CustomArticleLibraryVO[]> {
    if (!NumberUtils.isNumber(userId)) {
      return of(new Array<CustomArticleLibraryVO>());
    }
    return this.wagoApiClient.get(`custom-article/${userId}/library`)
      .pipe(map((response: any) => this.modelConversionService.convertCustomArticleLibraryVOs(response)));
  }

  public async getOwnCustomArticleLibrary(userId: number) {
    const customArticleLibraryVOs: CustomArticleLibraryVO[] = await lastValueFrom(this.getCustomArticleLibrariesByUserId(userId));
    let ownCustomArticleLibrary = customArticleLibraryVOs.find((collection: CustomArticleLibraryVO) => collection.user.id === userId);
    if (!ownCustomArticleLibrary) {
      ownCustomArticleLibrary = new CustomArticleLibraryVO();
    }
    return ownCustomArticleLibrary;
  }

  public requestLoadingCustomArticleImage(data: ImageProcessingData): Observable<ImageProcessingData> {
    if (!NumberUtils.isNumber(data.userId) || StringUtils.isEmpty(data.customImageHash)) {
      return of(data);
    }
    return this.loadCustomArticleImage(data.userId, data.customImageHash).pipe(
        map((customImageData: CustomImageDataVO) => {
          data.customImageData = customImageData;
          return data;
        }),
        catchError((err) => {
          Log.info('--Error while loading texture!');
          return of(data);
        })
      );
  }

  public loadCustomArticleImage(userId: number, imageHash: string): Observable<CustomImageDataVO | undefined> {
    if (!NumberUtils.isNumber(userId) || StringUtils.isEmpty(imageHash)) {
      return of(undefined);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('userId', userId.toString());
    httpParams = httpParams.append('imageHash', imageHash);
    const requestOptions = {
      params: httpParams
    };
    return this.wagoApiClient.get('custom-article/image', requestOptions,
      { isErrorHandlingRequired: false, askToElevateUserFromGuestByLoggingIn: true }).pipe(
      map((response: any) => {
        const image = this.modelConversionService.convertCustomImageDataVO(response);

        if(!image.isValidImage()) {
          return undefined;
        }

        return image;
      }));
  }

  public saveCustomArticleImage(customImageDataVO: CustomImageDataVO): void {
    if (!customImageDataVO) {
      this.saveCustomImageSubject.next(null);
      return;
    }
    this.saveCustomArticleImageObservable(customImageDataVO)
      .subscribe({
        next: (imageHash: string) => this.saveCustomImageSubject.next(imageHash),
        error: (error: any) => this.saveCustomImageSubject.next(null)
      });
  }

  public saveCustomArticleImageObservable(customImageDataVO: CustomImageDataVO): Observable<string> {
    if (!customImageDataVO) {
      return throwError(() => new Error('CustomImageDataVO is missing!'));
    }

    return this.wagoApiClient.post('custom-article/image', customImageDataVO, {responseType: 'text'});
  }

  public saveCustomArticle(customArticleRequest: SaveCustomArticleRequestVO): Observable<CustomArticleInfoVO> {
    if (!customArticleRequest) {
      return of(new CustomArticleInfoVO());
    }
    return this.wagoApiClient.post('custom-article/', customArticleRequest)
      .pipe(map((json: any) => {
        this._customArticlesChanged$.next();
        return this.modelConversionService.convertCustomArticleInfoVO(json);
      }));
  }

  public deleteCustomArticleById(customArticleId: number): Observable<any> {
    return this.wagoApiClient.delete(`custom-article/${customArticleId}`)
      .pipe(map((response) => response));
  }
}
